import {
  getAdvisorContextCopy,
  resolveAdvisorContext,
  type AdvisorContext,
  type DashboardSnapshot,
} from "./dashboard";

/**
 * Drawer-scoped Ask Sill contexts (plan 435 M6, task asd-store-context).
 *
 * These are entity-attached intents set via `useAdvisorDrawerStore.openDrawer
 * (context)` — distinct from `lib/dashboard.ts`'s pre-existing `AdvisorContext`,
 * the flat `?context=` URL enum the full chat page (`?view=advisor`) has
 * always read. The two context systems live side by side on purpose (M6
 * tech spec, Components table): the URL enum keeps deep links working
 * byte-for-byte, while drawer contexts carry real entity ids that must
 * NEVER enter the URL or `localStorage`/`sessionStorage` (roadmap, task
 * asd-store-context) — they live only in the in-memory
 * `useAdvisorDrawerStore`.
 */
export type AdvisorDrawerContext =
  | { kind: "offer"; id: string }
  | { kind: "agent"; id: string }
  | { kind: "tier_unlock" }
  | { kind: "home" };

export type AdvisorDrawerContextKind = AdvisorDrawerContext["kind"];

/**
 * A suggestion to SEED (prefill, never auto-send — roadmap, task
 * asd-store-context) the composer when the drawer opens with this context,
 * so a buyer who just clicked "Ask Sill" from a specific offer/agent/tier
 * row doesn't have to re-describe what they were looking at. `null` means
 * "no seed" — the drawer falls back to its generic empty-state prompts.
 */
export function seedSuggestionForContext(
  context: AdvisorDrawerContext | null,
): string | null {
  if (!context) return null;
  switch (context.kind) {
    case "home":
      return "What should I focus on next to get ready?";
    case "tier_unlock":
      return "What would it take to unlock the next readiness tier?";
    case "offer":
      return "Can you help me understand this offer?";
    case "agent":
      return "Can you tell me more about this agent?";
    default:
      return null;
  }
}

/** Either context system, resolved to one value for a render. */
export type AdvisorActiveContext = AdvisorDrawerContext | AdvisorContext;

/**
 * Resolves the active advisor context for a render: the drawer store's
 * `activeContext` wins when present, else the full page's legacy
 * `?context=` URL param. Pure — the store read and the `URLSearchParams`
 * read both belong to the caller.
 *
 * A page refresh loses the in-memory drawer context (never persisted, by
 * design — ids must not survive to the URL or storage), so a refreshed
 * full page falls back to the generic URL context (or `null`) without
 * error; it never throws on a missing context.
 *
 * SEAM for the integration pass (task asd-entry-repoint / the future
 * `AdvisorView` the M6 tech spec describes): this helper is intentionally
 * NOT wired into `components/dashboard/dashboard-views.tsx` by this lane
 * — that file is owned by the parallel Home-surfaces work in this cycle.
 * The call site should be:
 *   `resolveActiveAdvisorContext(useAdvisorDrawerStore.getState().activeContext, searchParams)`.
 */
export function resolveActiveAdvisorContext(
  storeContext: AdvisorDrawerContext | null,
  searchParams: URLSearchParams,
): AdvisorActiveContext {
  return storeContext ?? resolveAdvisorContext(searchParams);
}

/**
 * The analytics `context_kind` enum (task asd-analytics) — "none" when
 * nothing is active, else the drawer context's own `kind`, else the legacy
 * enum value itself (both systems already use short lowercase tokens, so
 * no translation table is needed).
 */
export function advisorContextKindOf(
  context: AdvisorActiveContext | null,
): AdvisorDrawerContextKind | Exclude<AdvisorContext, null> | "none" {
  if (!context) return "none";
  if (typeof context === "string") return context;
  return context.kind;
}

/**
 * Resolves the `{title, description}` the full page's `AdvisorContextRail`
 * renders, for EITHER context system (task asd-store-context /
 * asd-entry-repoint — `AdvisorView` reads `resolveActiveAdvisorContext`'s
 * output through this). The legacy enum delegates to the existing
 * `getAdvisorContextCopy`; the four drawer contexts get short, generic
 * copy — none of them carry enough caller-supplied detail (an offer id, an
 * agent id) to say more without a second fetch, and this rail is
 * decorative framing, not the conversation itself.
 */
export function advisorContextCopyFor(
  context: AdvisorActiveContext,
  snapshot: DashboardSnapshot,
): { description: string; title: string } {
  if (typeof context === "string" || context === null) {
    return getAdvisorContextCopy(context, snapshot);
  }
  switch (context.kind) {
    case "home":
      return {
        title: "Getting started",
        description:
          "Let's figure out what to focus on next to get ready.",
      };
    case "tier_unlock":
      return {
        title: "Unlocking your next tier",
        description:
          "Let's talk through what it takes to unlock your next readiness tier.",
      };
    case "offer":
      return {
        title: "This offer",
        description: "Let's go through what this offer means for you.",
      };
    case "agent":
      return {
        title: "This agent",
        description: "Let's talk about this agent and whether they're a fit.",
      };
  }
}
